// src/components/QueryInput/QueryInput.js
import React, { useState, useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import '../../App.css';
import { mimeTypeToLanguage } from './mimeTypeToLanguage';
import { API_URL } from './config';
import QueryDisplay from './QueryDisplay';
import QueryControls from './QueryControls';

const QueryInput = ({
  userId,
  files,
  updateEnergy,
  setCode,
  setIsApply,
  setPreviousCode,
  setSelectedFile,
  selectedFile,
  createFile,
  handleFileSelect,
  addDependencies,
  localInit,
  description,
  setLocalInit,
  initFiles
}) => {
  const [query, setQuery] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState('');
  const [shake, setShake] = useState(false);
  const [expandedIndex, setExpandedIndex] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [responseContent, setResponseContent] = useState([]);
  const [jsonStructures, setJsonStructures] = useState([]);
  const [initialized, setInitialized] = useState(false);
  const scrollViewRef = useRef(null);

  const scrollToBottom = () => {
    if (scrollViewRef.current) {
      scrollViewRef.current.scrollTop = scrollViewRef.current.scrollHeight;
    }
  };

  const setScrollViewRef = (node) => {
    scrollViewRef.current = node;
    if (loading) {
      scrollToBottom();
    }
  };

  const handleFileSelection = (event) => {
    const value = event.target.value;
    setSelectedFiles(value);
  };

  useEffect(() => {
    const executeQuery = async () => {
      if (localInit && files && files.length > 0) {
        await handleQuery(description);
      }
    };
  
    executeQuery();
  }, [localInit, description, files]);

  const prepareFormData = async (inputQuery) => {
    const formData = new FormData();
    formData.append('query', inputQuery);
    formData.append('userId', userId);
    setQuery('');

    if (localInit) {
      // Use initFiles if localInit is true
      initFiles.forEach((file) => {
        formData.append('files', file);
      });
    } else {
      if (selectedFiles.length === 0 && selectedFile) {
        const file = files.find(f => f.name === selectedFile);
        if (file) {
          formData.append('files', file);
        }
      } else {
        files.forEach((file) => {
          if (selectedFiles.includes(file.name)) {
            formData.append('files', file);
          }
        });
      }
    }

    return formData;
  };

  const handleQuery = async (inputQuery) => {

    if (!inputQuery) {
      setShake(true);
      setTimeout(() => setShake(false), 500);
      return;
    }

    if (!files || files.length === 0) {
      setError('Please upload at least one file.');
      return;
    }

    setLoading(true);
    if (!localInit) {
      setResponseContent(prevData => [...prevData, { "step": "query", "query": inputQuery }]);
    } else {
      inputQuery = `Initialize a complete boiler plate template for this user's request creating multiple files if necessary and setting up all initial components to either fully implement their request or provide a solid base for continual development. ${inputQuery}`;
    }

    const formData = await prepareFormData(inputQuery);

    try {
      const response = await fetch(API_URL, {
        method: 'POST',
        body: formData,
      });
      if (!response.ok) {
        const errorText = await response.text();
        setError(`Request failed with status ${response.status}: ${errorText}`);
        console.error('Response error:', errorText);
        setLoading(false);
        setCurrentStep("Failed");
        return;
      }
      setLoading(true);

      if (response.ok) {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.startsWith('text/event-stream')) {
          const reader = response.body.getReader();
          const decoder = new TextDecoder('utf-8');
          const readStream = async () => {
            let buffer = '';
            while (true) {
              const { done, value } = await reader.read();
              if (done) break;
              buffer += decoder.decode(value, { stream: true });
              let boundary = buffer.indexOf('\n\n');

              while (boundary !== -1) {
                const chunk = buffer.substring(0, boundary);
                buffer = buffer.substring(boundary + 2);
                if (chunk.startsWith('data: ')) {
                  const dataString = chunk.replace('data: ', '');
                  try {
                    const jsonData = JSON.parse(dataString);
                    setResponseContent(prevData => {
                      const lastElement = prevData[prevData.length - 1];
                      if (lastElement && lastElement["step"] === 'response' && jsonData["step"] === 'response') {
                        return [...prevData.slice(0, -1), jsonData];
                      } else {
                        return [...prevData, jsonData];
                      }
                    });
                  } catch (error) {
                    console.error("Failed to parse JSON:", error);
                    console.error("Data string:", dataString);
                  }
                }
                boundary = buffer.indexOf('\n\n');
              }
            }
            setLoading(false);
          };
          readStream();
        } else {
          const text = await response.text();
          setError('Expected an event stream but received a different content type.');
          console.error('Unexpected content type:', contentType, 'Response:', text);
          setLoading(false);
          setCurrentStep("Failed");
        }
      } else {
        const errorText = await response.text();
        setError(`Request failed with status ${response.status}: ${errorText}`);
        setLoading(false);
        setCurrentStep("Failed");
      }
    } catch (err) {
      setError(`An error occurred: ${err.message}`);
      setLoading(false);
      setCurrentStep("Failed");
    }
    setLocalInit(false);
  };

  const handleApplyChanges = (messageData) => {
    // Find the relevant file by matching the filename (stripping path to get final name).
    const file = files.find(f => f.name === messageData.filename.split('/').pop());
    if (!file) {
      console.error('File not found:', messageData.filename.split('/').pop());
      const contentt = messageData.updates[0].code_snippet;
      createFile(messageData.filename, contentt, messageData.mimetype);
      return;
    }
  
    const reader = new FileReader();
  
    reader.onload = (e) => {
      let fileContent = e.target.result;
      setSelectedFile(messageData.filename.split('/').pop());
      setPreviousCode(fileContent);
      setIsApply(true);
  
      // Convert file content to an array of lines.
      const lines = fileContent.split("\n");
  
      if (messageData && Array.isArray(messageData.updates)) {
        // Apply changes from bottom to top to preserve line numbering.
        for (let i = messageData.updates.length - 1; i >= 0; i--) {
          const change = messageData.updates[i];
          const { change_type } = change;
  
          // Convert line numbers from LLM's 0-based to editor's 1-based indexing
          const adjustLineNumber = (lineNum) => Math.max(0, lineNum + 0);
  
          // Logs for debugging
          console.log("Processing change:", change_type, change);
  
          if (change_type === 'single_line') {
            // single_line: { line, operation: 'replace'|'delete', code_snippet? }
            const { line, operation, code_snippet = '' } = change;
            // Convert from 1-based to 0-based index
            const index = line;
  
            if (index < 0 || index >= lines.length) {
              console.error(`Invalid line number ${line} for single_line change`);
              continue;
            }
  
            if (operation === 'replace') {
              // Replace exactly one line with new content
              lines.splice(index, 1, ...code_snippet.split("\n"));
            } else if (operation === 'delete') {
              // Remove that single line
              lines.splice(index, 1);
            }
          }
  
          else if (change_type === 'multi_line') {
            // multi_line: { start_line, end_line, operation: 'replace'|'delete', code_snippet? }
            const { start_line, end_line, operation, code_snippet = '' } = change;
            // Convert from 1-based to 0-based indices
            const startIndex = adjustLineNumber(start_line);
            const endIndex = adjustLineNumber(end_line + 1);
            const count = (endIndex - startIndex + 1);
  
            if (startIndex < 0 || endIndex >= lines.length || startIndex > endIndex) {
              console.error(`Invalid line range ${start_line}-${end_line} for multi_line change`);
              continue;
            }
  
            if (operation === 'replace') {
              // Replace the inclusive block [start_line, end_line]
              lines.splice(startIndex, count, ...code_snippet.split("\n"));
            } else if (operation === 'delete') {
              // Delete those lines
              lines.splice(startIndex, count);
            }
          }
  
          else if (change_type === 'insertion') {
            // insertion: { after_line, code_snippet }
            const { after_line, code_snippet } = change;
            // Convert from 1-based to 0-based index and adjust for insertion after the line
            const insertIndex = adjustLineNumber(after_line) + 1;
  
            if (insertIndex < 0 || insertIndex > lines.length) {
              console.error(`Invalid insertion point after line ${after_line}`);
              continue;
            }
  
            // Insert after the given line
            lines.splice(insertIndex, 0, ...code_snippet.split("\n"));
          }
  
          // Re-join into fileContent after each change so subsequent splices remain consistent
          fileContent = lines.join("\n");
          console.log(fileContent);
  
          // Log the change result for debugging
          console.log(`Applied ${change_type} change, new length: ${lines.length}`);
        }
      } else {
        console.error('messageData.updates is not an array or is undefined');
      }
  
      // Set the updated content
      setCode(fileContent);
    };
  
    reader.readAsText(file);
  };

  return (
    <Box
      className="queryInputContainer"
      sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        height: '95vh',
        width: '100%',
        overflow: 'hidden'
      }}
    >
      <Box
        ref={setScrollViewRef}
        sx={{
          flex: 1,
          overflowY: 'auto',
          width: '100%'
        }}
      >
        <QueryDisplay
          responseContent={responseContent}
          loading={loading}
          createFile={createFile}
          handleApplyChanges={handleApplyChanges}
          addDependencies={addDependencies}
          mimeTypeToLanguage={mimeTypeToLanguage}
        />
      </Box>
  
      <Box
        sx={{
          width: '100%',
          zIndex: 1,
          pb: '16px'
        }}
      >
        <QueryControls
          query={query}
          setQuery={setQuery}
          handleQuery={() => handleQuery(query)}
          files={files}
          selectedFile={selectedFile}
          selectedFiles={selectedFiles}
          handleFileSelection={handleFileSelection}
          loading={loading}
          shake={shake}
        />
      </Box>
    </Box>
  );
  

};

export default QueryInput;
